import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig.js";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { format, isWithinInterval } from "date-fns";

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const safeDate = (d) => {
  try { const p = new Date(d); return isNaN(p.getTime()) ? null : p; }
  catch { return null; }
};

const formatDate = (d) => {
  const p = safeDate(d);
  return p ? format(p, "dd MMM yyyy") : "—";
};

const getDaysUntil = (dateStr) => {
  const d = safeDate(dateStr);
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
};

const getUrgencyStyle = (days) => {
  if (days === null) return { color: "#94a3b8", bg: "#f1f5f9", label: "—" };
  if (days < 0)   return { color: "#94a3b8", bg: "#f1f5f9", label: "Past" };
  if (days === 0) return { color: "#dc2626", bg: "#fef2f2", label: "Today" };
  if (days <= 2)  return { color: "#dc2626", bg: "#fef2f2", label: `${days}d` };
  if (days <= 7)  return { color: "#d97706", bg: "#fffbeb", label: `${days}d` };
  return { color: "#059669", bg: "#ecfdf5", label: `${days}d` };
};

const CAR_COLORS = {
  "Saloon Car": { color: "#2563eb", bg: "#eff6ff" },
  "GMC":        { color: "#7c3aed", bg: "#f5f3ff" },
  "Hi-Ace":     { color: "#0891b2", bg: "#ecfeff" },
  "Coaster":    { color: "#d97706", bg: "#fffbeb" },
  "King Bus":   { color: "#dc2626", bg: "#fef2f2" },
  "H1-Van":     { color: "#059669", bg: "#ecfdf5" },
};

const getCarStyle = (car) => CAR_COLORS[car] || { color: "#64748b", bg: "#f1f5f9" };

// ─── Component ────────────────────────────────────────────────────────────────

const TransportSearch = () => {
  const [segments,      setSegments]      = useState([]);
  const [agentMap,      setAgentMap]      = useState({});
  const [loading,       setLoading]       = useState(true);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [startDate,     setStartDate]     = useState("");
  const [endDate,       setEndDate]       = useState("");
  const [showUpcoming,  setShowUpcoming]  = useState(true);
  const [search,        setSearch]        = useState("");
  const [sortDir,       setSortDir]       = useState("asc");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [transSnap, folderSnap] = await Promise.all([
          getDocs(collection(db, "TransportSegments")),
          getDocs(collection(db, "FOLDERS")),
        ]);

        const map = {};
        folderSnap.docs.forEach((d) => { map[d.data().FolderNo] = d.data().Agent; });
        setAgentMap(map);
        setSegments(transSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const uniqueAgents = useMemo(
    () => [...new Set(Object.values(agentMap).filter(Boolean))].sort(),
    [agentMap]
  );

  const filtered = useMemo(() => {
    let data = segments.map((s) => ({ ...s, Agent: agentMap[s.FolderNo] || "Unknown" }));

    if (selectedAgent) data = data.filter((s) => s.Agent === selectedAgent);

    if (startDate && endDate) {
      data = data.filter((s) => {
        const d = safeDate(s.date);
        return d && isWithinInterval(d, { start: new Date(startDate), end: new Date(endDate) });
      });
    }

    if (showUpcoming)
      data = data.filter((s) => {
        const d = safeDate(s.date);
        return d && d >= new Date(new Date().setHours(0, 0, 0, 0));
      });

    if (search) {
      const q = search.toLowerCase();
      data = data.filter((s) =>
        (s.FolderNo?.toString()   || "").includes(q) ||
        (s.Agent                  || "").toLowerCase().includes(q) ||
        (s.pickupLocation         || "").toLowerCase().includes(q) ||
        (s.dropoffLocation        || "").toLowerCase().includes(q) ||
        (s.carType                || "").toLowerCase().includes(q)
      );
    }

    data.sort((a, b) => {
      const da = safeDate(a.date), db_ = safeDate(b.date);
      if (!da || !db_) return 0;
      return sortDir === "asc" ? da - db_ : db_ - da;
    });

    return data;
  }, [segments, agentMap, selectedAgent, startDate, endDate, showUpcoming, search, sortDir]);

  const todayCount  = filtered.filter((s) => getDaysUntil(s.date) === 0).length;
  const urgentCount = filtered.filter((s) => { const d = getDaysUntil(s.date); return d !== null && d > 0 && d <= 2; }).length;
  const hasFilters  = selectedAgent || startDate || endDate || !showUpcoming || search;

  const clearFilters = () => {
    setSelectedAgent(""); setStartDate(""); setEndDate("");
    setShowUpcoming(true); setSearch("");
  };

  return (
    <>
      <GlobalStyle />
      <Page>

        {/* ── Header ── */}
        <PageHeader>
          <HeaderLeft>
            <PageLabel>Schedules</PageLabel>
            <PageTitle>Upcoming Transport</PageTitle>
            <PageSub>Track and monitor all booked transport segments</PageSub>
          </HeaderLeft>
          <StatsRow>
            <StatChip $color="#dc2626">
              <span className="val">{todayCount}</span>
              <span className="lbl">Today</span>
            </StatChip>
            <StatChip $color="#d97706">
              <span className="val">{urgentCount}</span>
              <span className="lbl">Next 48h</span>
            </StatChip>
            <StatChip $color="#2563eb">
              <span className="val">{filtered.length}</span>
              <span className="lbl">Showing</span>
            </StatChip>
          </StatsRow>
        </PageHeader>

        {/* ── Filter Bar ── */}
        <FilterCard>
          <FilterRow>
            <SearchBox>
              <SearchIcon>⌕</SearchIcon>
              <input
                type="text"
                placeholder="Search folder, agent, pickup, dropoff, car type…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchBox>

            <FilterField>
              <FilterLabel>Agent</FilterLabel>
              <StyledSelect value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)}>
                <option value="">All agents</option>
                {uniqueAgents.map((a) => <option key={a} value={a}>{a}</option>)}
              </StyledSelect>
            </FilterField>

            <FilterField>
              <FilterLabel>From date</FilterLabel>
              <DateInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </FilterField>

            <FilterField>
              <FilterLabel>To date</FilterLabel>
              <DateInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </FilterField>

            <FilterField style={{ justifyContent: "flex-end" }}>
              <ToggleRow>
                <ToggleLabel $active={showUpcoming}>
                  <ToggleSwitch $active={showUpcoming} onClick={() => setShowUpcoming((p) => !p)}>
                    <ToggleThumb $active={showUpcoming} />
                  </ToggleSwitch>
                  Upcoming only
                </ToggleLabel>
              </ToggleRow>
            </FilterField>

            {hasFilters && (
              <ClearBtn onClick={clearFilters}>✕ Clear</ClearBtn>
            )}
          </FilterRow>
        </FilterCard>

        {/* ── Table ── */}
        <TableCard>
          <TableTopBar>
            <TableCount>
              {loading ? "Loading…" : `${filtered.length} transfer${filtered.length !== 1 ? "s" : ""}`}
            </TableCount>
            <SortBtn onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}>
              {sortDir === "asc" ? "↑ Date asc" : "↓ Date desc"}
            </SortBtn>
          </TableTopBar>

          <TableWrap>
            <table>
              <thead>
                <tr>
                  <th>Folder</th>
                  <th>Agent</th>
                  <th>Car Type</th>
                  <th>Pickup</th>
                  <th>Dropoff</th>
                  <th>Date</th>
                  <th>In</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7}><LoadingRow>Loading transport data…</LoadingRow></td></tr>
                )}

                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState>
                        <div className="icon">⊡</div>
                        <div className="msg">No transport segments found</div>
                        <div className="sub">{hasFilters ? "Try adjusting your filters" : "No transport segments available"}</div>
                      </EmptyState>
                    </td>
                  </tr>
                )}

                {!loading && filtered.map((s) => {
                  const days    = getDaysUntil(s.date);
                  const urg     = getUrgencyStyle(days);
                  const car     = getCarStyle(s.carType);
                  const isToday = days === 0;

                  return (
                    <tr key={s.id} className={isToday ? "today-row" : ""}>
                      <td>
                        {/* <FolderChip>#{s.FolderNo}</FolderChip> */}
                     
                      <FolderChip
    onClick={() => {
      const url = `/FolderLedger?FolderNumber=${String(s.FolderNo)}`;
      window.open(url, "_blank");
    }}
    style={{ cursor: "pointer" }}
  >
    #{s.FolderNo}
  </FolderChip>
  
   </td>
                      <td>
                        <AgentCell>
                          <AgentAvatar>{(s.Agent || "?")[0].toUpperCase()}</AgentAvatar>
                          <span>{s.Agent}</span>
                        </AgentCell>
                      </td>
                      <td>
                        <CarBadge $color={car.color} $bg={car.bg}>{s.carType || "—"}</CarBadge>
                      </td>
                      <td>
                        <LocationCell>
                          <span className="pin">↑</span>
                          <span>{s.pickupLocation || "—"}</span>
                        </LocationCell>
                      </td>
                      <td>
                        <LocationCell>
                          <span className="pin drop">↓</span>
                          <span>{s.dropoffLocation || "—"}</span>
                        </LocationCell>
                      </td>
                      <td className="date">{formatDate(s.date)}</td>
                      <td>
                        <DaysBadge $color={urg.color} $bg={urg.bg}>{urg.label}</DaysBadge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrap>
        </TableCard>

      </Page>
    </>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 32px 28px 80px;
  max-width: 1250px;
  font-family: 'DM Sans', sans-serif;
  animation: ${fadeUp} 0.35s ease both;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div``;

const PageLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.3px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 18px;
  background: ${({ $color }) => $color}12;
  border: 1px solid ${({ $color }) => $color}30;
  border-radius: 10px;
  min-width: 70px;

  .val { font-size: 20px; font-weight: 600; color: ${({ $color }) => $color}; line-height: 1; }
  .lbl { font-size: 10px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: ${({ $color }) => $color}99; margin-top: 3px; }
`;

// ─── Filters ─────────────────────────────────────────────────────────────────

const FilterCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 2;
  min-width: 200px;

  input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 12px 9px 34px;
    font-size: 13.5px;
    font-family: 'DM Sans', sans-serif;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    color: #0f172a;
    outline: none;
    transition: all 0.15s;
    &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px #2563eb18; }
    &::placeholder { color: #cbd5e1; }
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
  pointer-events: none;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 140px;
  flex: 1;
`;

const FilterLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #64748b;
`;

const StyledSelect = styled.select`
  padding: 9px 28px 9px 12px;
  font-size: 13.5px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 10px center;
  color: #0f172a;
  outline: none;
  appearance: none;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.15s;
  &:focus { border-color: #2563eb; background-color: white; box-shadow: 0 0 0 3px #2563eb18; }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  font-size: 13.5px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  transition: all 0.15s;
  width: 100%;
  box-sizing: border-box;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px #2563eb18; }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  height: 38px;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $active }) => $active ? "#0f172a" : "#94a3b8"};
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
`;

const ToggleSwitch = styled.div`
  width: 36px; height: 20px;
  border-radius: 20px;
  background: ${({ $active }) => $active ? "#2563eb" : "#e2e8f0"};
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
`;

const ToggleThumb = styled.div`
  width: 14px; height: 14px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 3px;
  left: ${({ $active }) => $active ? "19px" : "3px"};
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const ClearBtn = styled.button`
  padding: 9px 14px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  align-self: flex-end;
  transition: all 0.15s;
  &:hover { background: #fee2e2; }
`;

// ─── Table ────────────────────────────────────────────────────────────────────

const TableCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
`;

const TableTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafafa;
`;

const TableCount = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
`;

const SortBtn = styled.button`
  font-size: 12px;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 4px 12px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #dbeafe; }
`;

const TableWrap = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    min-width: 720px;
  }

  thead tr {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    padding: 10px 14px;
    text-align: left;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #94a3b8;
    white-space: nowrap;
  }

  td {
    padding: 11px 14px;
    border-bottom: 0.5px solid #f1f5f9;
    vertical-align: middle;
    color: #334155;
  }

  tr:last-child td { border-bottom: none; }
  tr:hover td { background: #f8fafc; }
  tr.today-row td { background: #fffbeb; }
  tr.today-row:hover td { background: #fef3c7; }

  .date { font-family: 'DM Mono', monospace; font-size: 12px; color: #475569; white-space: nowrap; }
`;

const FolderChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 5px;
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  background: #f1f5f9;
  border: 0.5px solid #e2e8f0;
  color: #475569;
`;

const AgentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AgentAvatar = styled.div`
  width: 24px; height: 24px;
  border-radius: 6px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CarBadge = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const LocationCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #334155;

  .pin {
    font-size: 11px;
    font-weight: 700;
    color: #059669;
    flex-shrink: 0;
  }

  .pin.drop { color: #dc2626; }
`;

const DaysBadge = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 56px 24px;
  .icon { font-size: 32px; margin-bottom: 10px; opacity: 0.3; }
  .msg  { font-size: 15px; font-weight: 500; color: #64748b; }
  .sub  { font-size: 13px; color: #94a3b8; margin-top: 4px; }
`;

const LoadingRow = styled.div`
  text-align: center;
  padding: 48px;
  font-size: 13px;
  color: #94a3b8;
`;

export default TransportSearch;